import { detectTransferPairs, detectBankTransferPairs } from './csvParser';

export const UNCLASSIFIED_KEYS = new Set(['unclassified', 'unclassified_credit', 'unclassified_debit']);

// Build a Set of transaction IDs involved in any transfer pair (CC↔bank or bank↔bank).
// allTransactions must have _reportType and _reportName already attached.
export function buildTransferPairIds(allTransactions) {
  const ccPayments  = allTransactions.filter(tx => tx.transactionType === 'payment');
  const bankDebits  = allTransactions.filter(tx => tx.transactionType === 'debit');
  const bankTxs     = allTransactions.filter(tx => tx._reportType === 'bank');
  const ccBankPairs = detectTransferPairs(ccPayments, bankDebits);
  const bankPairs   = detectBankTransferPairs(bankTxs);
  const ids = new Set();
  for (const p of ccBankPairs) { ids.add(p.cc.id);    ids.add(p.bank.id);   }
  for (const p of bankPairs)   { ids.add(p.debit.id); ids.add(p.credit.id); }
  return ids;
}

// Classify a single transaction. Returns a new object with businessClassification set,
// or the original object if the classification is already definitive.
// tx must have _reportType attached. transferIds and allExpenseItems are pre-computed.
export function classifyTransaction(tx, { transferIds, allExpenseItems }) {
  if (tx.businessClassification && !UNCLASSIFIED_KEYS.has(tx.businessClassification)) return tx;

  if (tx._reportType === 'expense') return { ...tx, businessClassification: 'business_expense' };

  if (transferIds.has(tx.id) || tx.transactionType === 'payment') {
    return { ...tx, businessClassification: 'transfer' };
  }

  const isCredit = tx.transactionType === 'credit' || tx.transactionDirection === 'credit';
  if (isCredit) return { ...tx, businessClassification: 'unclassified_credit' };

  // Explicitly linked charges require manual classification — don't auto-promote via receipt matching
  if (tx.pairedWithId) return { ...tx, businessClassification: 'unclassified_debit' };

  // CC/bank debit matching a scanned receipt → business_expense.
  // Use convertedTotal so a €50 receipt can match a $54.50 CC charge.
  const txAmt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
  const matchesReceipt = allExpenseItems.some(e => {
    const eAmt = parseFloat(e.convertedTotal ?? e.total ?? 0);
    return Math.abs(eAmt - txAmt) <= 0.02
      && e.date && tx.date
      && Math.abs(new Date(e.date) - new Date(tx.date)) <= 3 * 86400000;
  });
  return { ...tx, businessClassification: matchesReceipt ? 'business_expense' : 'unclassified_debit' };
}

// Auto-classify all transaction files in savedFiles. Returns { [fileName]: updatedTransactions[] }
// for files that have changes only. Safe to call after every import.
export function autoClassifyFiles(savedFiles) {
  const expenseFiles = savedFiles.filter(f => !f.reportType || f.reportType === 'expense');
  const txFiles = savedFiles.filter(f => f.reportType === 'credit_card' || f.reportType === 'bank');

  if (!txFiles.length) return {};

  const allTransactions = txFiles.flatMap(f =>
    (f.data ?? []).map(tx => ({ ...tx, _reportName: f.name, _reportType: f.reportType }))
  );

  const transferIds     = buildTransferPairIds(allTransactions);
  const allExpenseItems = expenseFiles.flatMap(f => f.data ?? []);

  const result = {};
  for (const f of txFiles) {
    let changed = false;
    const updated = (f.data ?? []).map(tx => {
      const annotated = { ...tx, _reportName: f.name, _reportType: f.reportType };
      const classified = classifyTransaction(annotated, { transferIds, allExpenseItems });
      if (classified.businessClassification === tx.businessClassification) return tx;
      changed = true;
      return { ...tx, businessClassification: classified.businessClassification };
    });
    if (changed) result[f.name] = updated;
  }
  return result;
}
